import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CaseDiagnosis, User } from "pwapi";

import {
  editScreenDiagnoses,
  openDocument,
  initiateModifyReportProcedure,
} from "shared/actions/caseDetailActions";
import {
  useCurrentSpecimenDiagnoses,
  useLISFinalizedDiagnosis,
} from "shared/hooks/useDiagnosis";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import LinkButton from "components/LinkButton";
import SplitParagraphs from "components/SplitParagraphs";
import RequestScreenButton from "components/viewer/RequestScreenButton";

import { cptString } from "../DiagnosisDialogHelpers";
import { DialogEnum } from "../Dialog";
import Sidebar from "../Sidebar";
import SidebarFooter from "../SidebarFooter";
import { getHighestVersionDiagnosis, isFinalized } from "../utils";

export type ReadOnlyDiagnosisDialogProps = {
  caseDetailObject: CaseDetailState;
  slideDetailObject: SlideDetailState;
  user?: User;
  canAmendAddend?: boolean;
  canCorrectReport?: boolean;
  isSpecimenScreened?: boolean;
  isCaseScreened?: boolean;
  hat?: { text: string; style?: "warning" | "edit" };
  dialog?: DialogEnum | null;
  setDialog?: Function;
};

const ReadOnlyDiagnosisDialog = ({
  caseDetailObject,
  slideDetailObject,
  user,
  canAmendAddend,
  canCorrectReport,
  isSpecimenScreened,
  isCaseScreened,
  hat,
  dialog,
  setDialog,
}: ReadOnlyDiagnosisDialogProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const specimenDiagnoses = useCurrentSpecimenDiagnoses(
    caseDetailObject,
    slideDetailObject
  );
  const sentDiagnosis = getHighestVersionDiagnosis(specimenDiagnoses || []);
  const finalDiagnosis = useLISFinalizedDiagnosis(caseDetailObject);

  const isRecallReportPeriod = caseDetailObject.transmitting_at
    ? DateTime.now() < DateTime.fromISO(caseDetailObject.transmitting_at)
    : false;

  const { diagnosis, microscopic, comment, icd10_code, zsd_code, cpt_codes } =
    sentDiagnosis || {};

  const onHandleModifyReport = (
    source:
      | CaseDiagnosis.source.ADDEND
      | CaseDiagnosis.source.AMEND
      | CaseDiagnosis.source.CORRECTION
  ) => {
    if (caseDetailObject?.id)
      dispatch(initiateModifyReportProcedure(caseDetailObject.id, source));
  };

  const onHandleEditScreen = () => {
    if (caseDetailObject?.id) {
      dispatch(editScreenDiagnoses(caseDetailObject.id));
    }
  };

  return (
    <Sidebar
      hasAutoWidth
      isScrollable
      hat={
        !!hat && (
          <div
            className={classNames("FinalizedDiagnosis__header", {
              "FinalizedDiagnosis__header--warning": hat.style === "warning",
              "FinalizedDiagnosis__header--edit": hat.style === "edit",
            })}
          >
            {hat.style && (
              <FontAwesomeIcon
                icon={hat.style === "edit" ? "pencil" : "exclamation"}
              />
            )}
            <span>{hat.text}</span>
          </div>
        )
      }
    >
      <div className="Sidebar__contentContainer DiagnosisDialog DiagnosisDialog--readonly">
        <dl className="DiagnosisDialog__diagnosis">
          <dt>Diagnosis</dt>
          <dd>
            {diagnosis?.split("\n").map((d, i) => (
              <p key={i}>{d}</p>
            ))}
          </dd>
        </dl>
        <dl className="DiagnosisDialog__findings">
          <dt>Microscopic Findings</dt>
          <dd>
            <SplitParagraphs paragraph={microscopic || ""} />
          </dd>
        </dl>
        <dl className="DiagnosisDialog__comments">
          <dt>Comments</dt>
          <dd>{comment}</dd>
        </dl>
        <dl className="DiagnosisDialog__codes">
          <div>
            <dt>ICD 10</dt>
            <dd>{icd10_code}</dd>
          </div>

          <div>
            <dt>ZSD</dt>
            <dd>{zsd_code}</dd>
          </div>

          <div>
            <dt>CPT Codes</dt>
            <dd>{cpt_codes?.map((c) => cptString(c)).join(", ")}</dd>
          </div>

          {finalDiagnosis?.finalized_report_id && (
            <div>
              <dt>
                Diagnosis {slideDetailObject.specimen?.name} is shown for
                convenience:
              </dt>
              <dd>
                <LinkButton
                  title="Download Report"
                  className="Button Button--blue"
                  onClick={() => {
                    caseDetailObject.id &&
                      finalDiagnosis?.finalized_report_id &&
                      dispatch(
                        openDocument(
                          caseDetailObject.id,
                          finalDiagnosis.finalized_report_id
                        )
                      );
                  }}
                >
                  Final Report &nbsp; <FontAwesomeIcon icon="file-download" />
                </LinkButton>
              </dd>
            </div>
          )}
        </dl>
      </div>
      {isFinalized(caseDetailObject) ? (
        (canAmendAddend || canCorrectReport) && !isRecallReportPeriod ? (
          <SidebarFooter>
            {canCorrectReport && (
              <PathologyWatchButton
                onClick={() =>
                  onHandleModifyReport(CaseDiagnosis.source.CORRECTION)
                }
                color="green"
              >
                Correct Report
              </PathologyWatchButton>
            )}
            {canAmendAddend && (
              <>
                <PathologyWatchButton
                  onClick={() => {
                    onHandleModifyReport(CaseDiagnosis.source.AMEND);
                  }}
                  color="green"
                >
                  Amend
                </PathologyWatchButton>
                <PathologyWatchButton
                  onClick={() => {
                    onHandleModifyReport(CaseDiagnosis.source.ADDEND);
                  }}
                  color="green"
                >
                  Addend
                </PathologyWatchButton>
              </>
            )}
          </SidebarFooter>
        ) : undefined
      ) : isSpecimenScreened ? (
        <SidebarFooter>
          <PathologyWatchButton
            onClick={() => {
              onHandleEditScreen();
            }}
            color="grey"
          >
            Edit Screen
          </PathologyWatchButton>
          <PathologyWatchButton
            onClick={() => navigate(`/cases/${caseDetailObject.id}/preview`)}
            color="green"
          >
            Preview for Sign Out &rarr;
          </PathologyWatchButton>
        </SidebarFooter>
      ) : isCaseScreened && specimenDiagnoses && !specimenDiagnoses.length ? (
        <SidebarFooter>
          <RequestScreenButton
            caseDetailObject={caseDetailObject}
            slideId={slideDetailObject?.id}
            currentUser={user}
            dialog={dialog}
            setDialog={setDialog}
          />
        </SidebarFooter>
      ) : undefined}
    </Sidebar>
  );
};

export default ReadOnlyDiagnosisDialog;
